/**
 * Therapist Simulation Lab — entrypoint for clinical therapists to run
 * platform-authored training scenarios as personal practice.
 *
 * Filtered by the simulationResolver: shows scenarios in (defaults ∪ grants)
 * ∩ library only. Starts a TrainingSession with mode='therapist_practice',
 * then navigates to the existing /training/session/:id flow.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Card, CardContent, Chip, Button, Alert, CircularProgress,
  Grid, Snackbar, IconButton, Tooltip, Avatar
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import api from '../../services/api';
import ScenarioPreviewDrawer from '../../components/therapist/ScenarioPreviewDrawer';

const VISIBILITY_DESCRIPTIONS = {
  auto: 'AI scoring is shown to you immediately after the session.',
  supervisor_gated: 'Your supervisor reviews scoring and releases the AI evaluation when ready.',
  never: 'AI scoring is captured for QA but not shown to you.',
};

export default function TherapistSimulationLab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(null);
  const [toast, setToast] = useState(null);
  const [previewScenario, setPreviewScenario] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/simulation-governance/therapist/available', token);
        if (cancelled) return;
        setData(res?.data || null);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || err?.message || 'Could not load library');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  async function start(scenarioId) {
    setStarting(scenarioId);
    try {
      const res = await api.post('/training/sessions/start',
        { scenarioId, mode: 'therapist_practice' },
        token
      );
      const session = res?.data?.data?.session;
      const sid = session?._id || session?.id;
      if (sid) navigate(`/training/session/${sid}`);
      else setToast('Session created but ID was missing — refresh the page.');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Could not start session';
      setError(msg);
    } finally {
      setStarting(null);
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const scenarios = data?.scenarios?.list || [];
  const methodologies = data?.methodologies?.list || [];
  const visibility = data?.feedbackVisibility || 'supervisor_gated';

  // Build a lookup so the preview drawer can show the rubric name without
  // a second round-trip.
  const methodologyById = new Map(
    methodologies.map(m => [String(m._id), m])
  );
  const rubricNameFor = (scenario) => {
    if (!scenario?.defaultMethodologyId) return null;
    const m = methodologyById.get(String(scenario.defaultMethodologyId));
    return m ? `${m.name}${m.organisation ? ` — ${m.organisation}` : ''}` : null;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="h4" fontWeight={600}>Simulation Lab</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Platform-authored practice scenarios. Each session is private to you and any AI
              evaluation is governed by your supervisor's settings.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate('/therapist/simulation-lab/history')}
          >
            My practice history
          </Button>
        </Box>

        <Alert severity="info">
          AI feedback visibility: <strong>{visibility.replace(/_/g, ' ')}</strong>
          {VISIBILITY_DESCRIPTIONS[visibility] && ` — ${VISIBILITY_DESCRIPTIONS[visibility]}`}
        </Alert>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {scenarios.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                No scenarios in your library yet. Ask your clinical supervisor to grant scenarios on
                your profile, or your clinic admin to license platform scenarios into the group library.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {scenarios.map((s) => {
              const ctx = s.scenarioContext || {};
              const visibilityLevel = s.visibilityLevel || 'full_brief';
              const isBlind = visibilityLevel === 'blind';
              const isReferralOnly = visibilityLevel === 'referral_only';
              const objectiveCount = (s.learningObjectives || []).filter(Boolean).length;
              const oneLine = (
                isBlind ? null
                  : isReferralOnly ? (s.referralSummary || ctx.referralSource)
                  : (ctx.presentingComplaint || s.description)
              );
              return (
                <Grid item xs={12} sm={6} lg={4} key={s._id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: 1, pb: 1 }}>
                      {/* Title row with optional avatar */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        {s.personaImageUrl && (
                          <Avatar
                            src={s.personaImageUrl}
                            alt={s.name}
                            sx={{ width: 56, height: 56, flexShrink: 0 }}
                            variant="rounded"
                          />
                        )}
                        <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3, flex: 1 }}>
                          {s.name}
                        </Typography>
                      </Box>

                      {/* Metadata chips */}
                      <Stack direction="row" spacing={0.5} sx={{ mt: 1, gap: 0.5 }} flexWrap="wrap">
                        {s.difficulty && (
                          <Chip size="small" label={s.difficulty} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                        )}
                        {s.category && (
                          <Chip size="small" label={(s.category || '').replace(/_/g, ' ')} variant="outlined" />
                        )}
                        {ctx.setting && (
                          <Chip size="small" label={(ctx.setting || '').replace(/_/g, ' ')} variant="outlined" />
                        )}
                        {isBlind && (
                          <Chip size="small" icon={<VisibilityOffIcon />} label="blind" color="secondary" />
                        )}
                        {isReferralOnly && (
                          <Chip size="small" icon={<DescriptionIcon />} label="referral only" color="info" />
                        )}
                      </Stack>

                      {/* Single-line preview */}
                      {oneLine && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {oneLine}
                        </Typography>
                      )}

                      {/* Footer metadata row */}
                      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} alignItems="center">
                        {ctx.timeLimit && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {ctx.timeLimit} min
                            </Typography>
                          </Stack>
                        )}
                        {objectiveCount > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {objectiveCount} objective{objectiveCount === 1 ? '' : 's'}
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>

                    {/* Actions */}
                    <Stack direction="row" spacing={1} sx={{ p: 2, pt: 0 }}>
                      <Tooltip title="Preview brief">
                        <IconButton
                          onClick={() => setPreviewScenario(s)}
                          size="small"
                          sx={{ border: 1, borderColor: 'divider' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={starting === String(s._id) ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                        onClick={() => start(String(s._id))}
                        disabled={!!starting}
                        size="medium"
                      >
                        {starting === String(s._id) ? 'Starting…' : 'Start practice'}
                      </Button>
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Stack>

      <ScenarioPreviewDrawer
        scenario={previewScenario}
        open={!!previewScenario}
        onClose={() => setPreviewScenario(null)}
        onStart={(id) => { setPreviewScenario(null); start(id); }}
        starting={previewScenario && starting === String(previewScenario._id) ? starting : null}
        rubricName={rubricNameFor(previewScenario)}
      />

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}
